import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, ChevronsUpDown, Check, Download, FileSpreadsheet, File, FileType, FileJson } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicidadRegistro = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [contribuyentes, setContribuyentes] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [clasificadorImpPub, setClasificadorImpPub] = useState([]);
  const [regFilters, setRegFilters] = useState({ clase: "", rif: "", nombre: "", tipo_aviso: "", medidas: "" });

  useEffect(() => { fetchRegistros(); }, []);

  const fetchRegistros = async () => {
    try { const r = await axios.get(`${API}/api/publicidad/registro`, { headers }); setItems(r.data); }
    catch { toast.error("Error al cargar registros"); }
  };
  const fetchContribuyentes = async (clase) => {
    try {
      const claseParam = clase === "Contribuyente sin LAE" ? "sin-lae" : "con-lae";
      const r = await axios.get(`${API}/api/contribuyentes/buscar?clase=${claseParam}`, { headers });
      setContribuyentes(r.data);
    }
    catch { setContribuyentes([]); }
  };
  const fetchClasificadorImpPub = async () => {
    try { const r = await axios.get(`${API}/api/publicidad/clasificador-impuestos`, { headers }); setClasificadorImpPub(r.data); }
    catch { setClasificadorImpPub([]); }
  };

  const handleSave = async () => {
    if (!formData.rif) { toast.error("RIF / Contribuyente es obligatorio"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/api/publicidad/registro/${editingItem.id}`, formData, { headers });
        toast.success("Registro actualizado exitosamente");
      } else {
        await axios.post(`${API}/api/publicidad/registro`, formData, { headers });
        toast.success("Registro creado exitosamente");
      }
      setDialogOpen(false); setFormData({}); setEditingItem(null); fetchRegistros();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const openNew = () => {
    setEditingItem(null);
    setFormData({ clase_contribuyente: "Contribuyente con LAE", lineas_impuestos: [], fecha: new Date().toISOString().split("T")[0] });
    fetchContribuyentes("Contribuyente con LAE"); fetchClasificadorImpPub();
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item, clase_contribuyente: item.clase_contribuyente || "Contribuyente con LAE" });
    fetchContribuyentes(item.clase_contribuyente || "Contribuyente con LAE"); fetchClasificadorImpPub();
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    try { await axios.delete(`${API}/api/publicidad/registro/${itemToDelete.id}`, { headers }); toast.success("Registro eliminado"); setDeleteDialogOpen(false); setItemToDelete(null); fetchRegistros(); }
    catch { toast.error("Error al eliminar"); }
  };

  const filtered = items.filter(item => {
    const rf = regFilters;
    if (rf.clase && !(item.clase_contribuyente || "").toLowerCase().includes(rf.clase.toLowerCase())) return false;
    if (rf.rif && !(item.rif || "").toLowerCase().includes(rf.rif.toLowerCase())) return false;
    if (rf.nombre && !(item.nombre_razon_social || "").toLowerCase().includes(rf.nombre.toLowerCase())) return false;
    if (rf.tipo_aviso) {
      const avisos = item.avisos || [{ tipo_aviso: item.tipo_aviso }];
      if (!avisos.some(a => (a.tipo_aviso || "").toLowerCase().includes(rf.tipo_aviso.toLowerCase()))) return false;
    }
    if (rf.medidas) {
      const avisos = item.avisos || [{ metros_ancho: item.metros_ancho, metros_largo: item.metros_largo }];
      if (!avisos.some(a => `${a.metros_ancho ?? ""}x${a.metros_largo ?? ""}`.includes(rf.medidas) || `${a.metros_ancho ?? ""} x ${a.metros_largo ?? ""}`.includes(rf.medidas))) return false;
    }
    return true;
  });

  // ============ EXPORT FUNCTIONS ============
  const getExportData = () => {
    return filtered.map(item => {
      const avisos = item.avisos || [{ tipo_aviso: item.tipo_aviso, metros_ancho: item.metros_ancho, metros_largo: item.metros_largo }];
      return {
        "Clase": item.clase_contribuyente || "",
        "RIF": item.rif || "",
        "Contribuyente": item.nombre_razon_social || "",
        "Tipo de Aviso": avisos.map(a => a.tipo_aviso || "").join(", "),
        "Medidas": avisos.map(a => `${a.metros_ancho ?? "-"}x${a.metros_largo ?? "-"} m`).join(", ")
      };
    });
  };

  const exportToExcel = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registro Publicidad");
    XLSX.writeFile(wb, `registro_publicidad_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportToPDF = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const doc = new jsPDF("landscape");
    doc.setFontSize(16);
    doc.text("Registro de Publicidad", 14, 20);
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")}`, 14, 28);
    autoTable(doc, {
      head: [Object.keys(data[0])],
      body: data.map(row => Object.values(row)),
      startY: 35,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [124, 58, 237] }
    });
    doc.save(`registro_publicidad_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportToCSV = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + headers + "\n" + rows], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `registro_publicidad_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV exitosamente");
  };

  const exportToText = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const headers = Object.keys(data[0]);
    const colWidths = headers.map(h => Math.max(h.length, ...data.map(row => String(row[h]).length)));
    let text = "REGISTRO DE PUBLICIDAD\n";
    text += `Fecha: ${new Date().toLocaleDateString("es-VE")}\n`;
    text += "=".repeat(Math.max(colWidths.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n";
    text += headers.map((h, i) => h.padEnd(colWidths[i])).join(" | ") + "\n";
    text += colWidths.map(w => "-".repeat(w)).join("-+-") + "\n";
    data.forEach(row => { text += headers.map((h, i) => String(row[h]).padEnd(colWidths[i])).join(" | ") + "\n"; });
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `registro_publicidad_${new Date().toISOString().split("T")[0]}.txt`);
    toast.success("Exportado a Texto exitosamente");
  };

  const exportToHTML = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const headers = Object.keys(data[0]);
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Registro de Publicidad</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#7c3aed}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:10px;text-align:left;font-size:12px}th{background-color:#7c3aed;color:white}tr:nth-child(even){background-color:#f9fafb}tr:hover{background-color:#f3f4f6}.fecha{color:#6b7280;margin-bottom:20px}</style></head><body><h1>Registro de Publicidad</h1><p class="fecha">Fecha: ${new Date().toLocaleDateString("es-VE")}</p><table><thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${data.map(row => `<tr>${Object.values(row).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `registro_publicidad_${new Date().toISOString().split("T")[0]}.html`);
    toast.success("Exportado a HTML exitosamente");
  };

  const exportToJSON = () => {
    const data = getExportData();
    if (!data.length) { toast.error("No hay datos para exportar"); return; }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    saveAs(blob, `registro_publicidad_${new Date().toISOString().split("T")[0]}.json`);
    toast.success("Exportado a JSON exitosamente");
  };

  return (
    <div className="space-y-6" data-testid="publicidad-registro-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-800">Registro de Publicidad</h1><p className="text-slate-500">Gestión de registros de publicidad comercial</p></div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filtered.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{items.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-exportar-reg-pub">
                <Download className="w-4 h-4" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> Texto</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" /> HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openNew} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-nuevo-registro"><Plus className="w-4 h-4 mr-2" /> Nuevo</Button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50"><TableHead>Clase</TableHead><TableHead>RIF</TableHead><TableHead>Contribuyente</TableHead><TableHead>Tipo de Aviso</TableHead><TableHead>Medidas</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow>
            <TableRow className="bg-slate-50/50 border-b">
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regFilters.clase} onChange={(e) => setRegFilters({...regFilters, clase: e.target.value})} data-testid="filter-reg-clase" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regFilters.rif} onChange={(e) => setRegFilters({...regFilters, rif: e.target.value})} data-testid="filter-reg-rif" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regFilters.nombre} onChange={(e) => setRegFilters({...regFilters, nombre: e.target.value})} data-testid="filter-reg-nombre" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regFilters.tipo_aviso} onChange={(e) => setRegFilters({...regFilters, tipo_aviso: e.target.value})} data-testid="filter-reg-tipo-aviso" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regFilters.medidas} onChange={(e) => setRegFilters({...regFilters, medidas: e.target.value})} data-testid="filter-reg-medidas" /></TableHead>
              <TableHead className="px-3 py-1.5"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No hay registros</TableCell></TableRow>
            ) : filtered.map((item) => {
              const avisos = item.avisos || [{ metros_ancho: item.metros_ancho, metros_largo: item.metros_largo, tipo_aviso: item.tipo_aviso }];
              return (
              <TableRow key={item.id}>
                <TableCell className="text-sm">{item.clase_contribuyente}</TableCell>
                <TableCell className="font-mono text-sm">{item.rif}</TableCell>
                <TableCell className="text-sm">{item.nombre_razon_social || "-"}</TableCell>
                <TableCell className="text-sm">{avisos.map((a, i) => (<div key={i} className={i > 0 ? "mt-1 pt-1 border-t border-slate-100" : ""}><span className="text-slate-700">{a.tipo_aviso || "-"}</span></div>))}</TableCell>
                <TableCell className="text-sm">{avisos.map((a, i) => (<div key={i} className={i > 0 ? "mt-1 pt-1 border-t border-slate-100" : ""}><span className="text-slate-400 text-xs">{a.metros_ancho ?? "-"} x {a.metros_largo ?? "-"} m</span></div>))}</TableCell>
                <TableCell className="text-right"><div className="flex justify-end gap-1"><Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`btn-edit-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button><Button variant="ghost" size="icon" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} data-testid={`btn-delete-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-purple-600" />{editingItem ? "Editar Registro" : "Nuevo Registro de Publicidad"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Clase de Contribuyente</Label><Select value={formData.clase_contribuyente || "Contribuyente con LAE"} onValueChange={(value) => { setFormData({...formData, clase_contribuyente: value, rif: "", nombre_razon_social: ""}); fetchContribuyentes(value); }}><SelectTrigger data-testid="form-clase-contribuyente"><SelectValue placeholder="Seleccione clase" /></SelectTrigger><SelectContent><SelectItem value="Contribuyente con LAE">Contribuyente con LAE</SelectItem><SelectItem value="Contribuyente sin LAE">Contribuyente sin LAE</SelectItem></SelectContent></Select></div>
            <div className="space-y-2">
              <Label>RIF / Contribuyente *</Label>
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild><Button variant="outline" role="combobox" className="w-full justify-between h-auto min-h-[40px] text-left font-normal" data-testid="form-rif-contribuyente">{formData.rif ? `${formData.rif} - ${formData.nombre_razon_social || ""}` : "Seleccione contribuyente"}<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" /></Button></PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start"><Command><CommandInput placeholder="Buscar por RIF o nombre..." /><CommandList><CommandEmpty>No se encontraron contribuyentes</CommandEmpty><CommandGroup>
                  {contribuyentes.map((c) => (<CommandItem key={c.id || c.rif} value={`${c.display_name || c.rif}`} onSelect={() => { const rifFull = c.rif.includes("-") ? c.rif : `${c.tipo_rif || "J"}-${c.rif}`; const updates = { ...formData, rif: rifFull, nombre_razon_social: c.nombre || c.razon_social || c.denominacion_comercial || "" }; if (c.trimestre_publicidad) updates.trimestre_publicidad = parseInt(c.trimestre_publicidad) || null; if (c.anio_publicidad) updates.anio_publicidad = parseInt(c.anio_publicidad) || null; setFormData(updates); setRifPopoverOpen(false); }}><Check className={`mr-2 h-4 w-4 ${formData.rif === c.rif ? "opacity-100" : "opacity-0"}`} /><span className="text-sm">{c.display_name || c.rif}</span></CommandItem>))}
                </CommandGroup></CommandList></Command></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">Avisos</Label>
                <Button size="sm" variant="outline" onClick={() => { const avisos = [...(formData.avisos || [{ metros_ancho: null, metros_largo: null, tipo_aviso: "" }])]; avisos.push({ metros_ancho: null, metros_largo: null, tipo_aviso: "" }); setFormData({ ...formData, avisos }); }} data-testid="btn-add-aviso"><Plus className="w-3 h-3 mr-1" /> Agregar Aviso</Button>
              </div>
              {(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }]).map((aviso, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-3 bg-slate-50/50 relative">
                  {(formData.avisos || []).length > 1 && (
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6" onClick={() => { const avisos = [...(formData.avisos || [])]; avisos.splice(idx, 1); setFormData({ ...formData, avisos }); }} data-testid={`btn-remove-aviso-${idx}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  )}
                  {(formData.avisos || []).length > 1 && <span className="text-xs font-medium text-slate-500">Aviso {idx + 1}</span>}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Metros de Ancho</Label><Input type="number" step="0.01" value={aviso.metros_ancho ?? ""} onChange={(e) => { const avisos = [...(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }])]; avisos[idx] = { ...avisos[idx], metros_ancho: parseFloat(e.target.value) || null }; setFormData({ ...formData, avisos }); }} placeholder="Ej: 2.5" data-testid={`form-metros-ancho-${idx}`} /></div>
                    <div className="space-y-1"><Label className="text-xs">Metros de Largo</Label><Input type="number" step="0.01" value={aviso.metros_largo ?? ""} onChange={(e) => { const avisos = [...(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }])]; avisos[idx] = { ...avisos[idx], metros_largo: parseFloat(e.target.value) || null }; setFormData({ ...formData, avisos }); }} placeholder="Ej: 3.0" data-testid={`form-metros-largo-${idx}`} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Tipo de Aviso</Label>
                      <Select value={aviso.tipo_aviso || ""} onValueChange={(v) => { const avisos = [...(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }])]; avisos[idx] = { ...avisos[idx], tipo_aviso: v }; setFormData({ ...formData, avisos }); }}>
                        <SelectTrigger data-testid={`form-tipo-aviso-${idx}`}><SelectValue placeholder="Seleccione tipo de aviso" /></SelectTrigger>
                        <SelectContent>{clasificadorImpPub.map((c) => <SelectItem key={c.id} value={c.nombre_impuesto}>{c.nombre_impuesto}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Último trimestre y año de pago impuesto</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={aviso.ultimo_trimestre_pago ? String(aviso.ultimo_trimestre_pago) : ""} onValueChange={(v) => { const avisos = [...(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }])]; avisos[idx] = { ...avisos[idx], ultimo_trimestre_pago: parseInt(v) || null }; setFormData({ ...formData, avisos }); }}>
                          <SelectTrigger data-testid={`form-ultimo-trim-${idx}`}><SelectValue placeholder="Trim" /></SelectTrigger>
                          <SelectContent><SelectItem value="1">1er</SelectItem><SelectItem value="2">2do</SelectItem><SelectItem value="3">3er</SelectItem><SelectItem value="4">4to</SelectItem></SelectContent>
                        </Select>
                        <Input type="number" value={aviso.ultimo_anio_pago ?? ""} onChange={(e) => { const avisos = [...(formData.avisos || [{ metros_ancho: formData.metros_ancho, metros_largo: formData.metros_largo, tipo_aviso: formData.tipo_aviso || "" }])]; avisos[idx] = { ...avisos[idx], ultimo_anio_pago: parseInt(e.target.value) || null }; setFormData({ ...formData, avisos }); }} placeholder="Año" data-testid={`form-ultimo-anio-pago-${idx}`} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border rounded-lg p-3 space-y-2 bg-blue-50/50">
              <Label className="text-sm font-semibold text-blue-700">Último Trimestre y Año de Pago de Tasa</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Trimestre</Label>
                  <Select value={formData.trimestre_publicidad ? String(formData.trimestre_publicidad) : ""} onValueChange={(v) => setFormData({...formData, trimestre_publicidad: parseInt(v) || null})}>
                    <SelectTrigger data-testid="form-trimestre-publicidad"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                    <SelectContent><SelectItem value="1">1er</SelectItem><SelectItem value="2">2do</SelectItem><SelectItem value="3">3er</SelectItem><SelectItem value="4">4to</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Año</Label>
                  <Input type="number" value={formData.anio_publicidad ?? ""} onChange={(e) => setFormData({...formData, anio_publicidad: parseInt(e.target.value) || null})} placeholder="Ej: 2025" data-testid="form-anio-publicidad" />
                </div>
              </div>
              <p className="text-xs text-blue-500">Se auto-llena al seleccionar contribuyente con LAE. Para contribuyente sin LAE, llene manualmente.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Confirmar Eliminacion</DialogTitle></DialogHeader><p className="text-sm text-slate-600">Esta seguro de eliminar este registro?</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button><Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete">Eliminar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicidadRegistro;
